import React, { useState } from 'react'
import { CalendarClock, MapPin } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import {
  useCancelMilestoneDateProposalMutation,
  useCreateMilestoneDateProposalMutation,
  useGetCollaborationEventsQuery,
  useRsvpCollaborationEventMutation,
  useSubmitMilestoneCheckinMutation,
  type MilestoneCheckIn,
  type MilestoneDateProposal,
} from '@/redux/collaborationsApi'
import { Card, Eyebrow, Pill, day, fieldCls, linkCls, primaryCls, secondaryCls } from './ui'

const fail = (err: any) =>
  toast.error('That didn’t go through', { description: err?.data?.error ?? err?.message })

/** The brand asked how it's going on this step: on track, or need more time? */
export const CheckInPrompt: React.FC<{
  dealId: string
  milestoneId: string
  checkIn: MilestoneCheckIn | null | undefined
  brandName: string
}> = ({ dealId, milestoneId, checkIn, brandName }) => {
  const [answer, { isLoading }] = useSubmitMilestoneCheckinMutation()
  if (!checkIn?.active) return null
  if (checkIn.response) {
    return (
      <p className="mt-3 text-xs text-muted-foreground">
        You told {brandName} you're {checkIn.response === 'on_track' ? 'on track' : 'running late'}
        {checkIn.responseAt ? ` on ${day(checkIn.responseAt)}` : ''}.
      </p>
    )
  }
  const send = (response: 'on_track' | 'needs_more_time') =>
    answer({ campaignId: dealId, milestoneId, response })
      .unwrap()
      .then(() => toast('Thanks, the brand knows'))
      .catch(fail)
  return (
    <div className="mt-3 rounded-xl border border-[#BFD7FB] bg-[#EFF6FF] p-3">
      <Eyebrow>{brandName} checked in</Eyebrow>
      <p className="mt-1 text-sm text-foreground">How's this step going?</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          className={primaryCls}
          disabled={isLoading}
          onClick={() => send('on_track')}
        >
          On track
        </button>
        <button
          type="button"
          className={secondaryCls}
          disabled={isLoading}
          onClick={() => send('needs_more_time')}
        >
          Need more time
        </button>
      </div>
    </div>
  )
}

/** Ask the brand for a later due date, with a reason; one open proposal at a time */
export const MoreTime: React.FC<{
  dealId: string
  milestoneId: string
  dueDate?: string | null
  proposal: MilestoneDateProposal | null | undefined
  brandName: string
}> = ({ dealId, milestoneId, dueDate, proposal, brandName }) => {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const [create, { isLoading }] = useCreateMilestoneDateProposalMutation()
  const [cancel] = useCancelMilestoneDateProposalMutation()

  if (proposal?.status === 'open') {
    return (
      <div className="mt-3 flex items-start justify-between gap-3 rounded-xl bg-muted p-3 text-sm">
        <div>
          <p className="font-semibold text-foreground">
            You asked for {day(proposal.proposedDueDate)}
          </p>
          <p className="text-xs text-muted-foreground">
            {brandName} is deciding. {proposal.reason ? `“${proposal.reason}”` : ''}
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs font-semibold text-muted-foreground underline"
          onClick={() =>
            cancel({ campaignId: dealId, milestoneId, proposalId: proposal.id })
              .unwrap()
              .catch(fail)
          }
        >
          Withdraw
        </button>
      </div>
    )
  }
  if (proposal?.status === 'declined') {
    return (
      <p className="mt-3 text-xs text-muted-foreground">
        {brandName} kept the date{proposal.responseNote ? `: “${proposal.responseNote}”` : '.'}
      </p>
    )
  }
  if (!open) {
    return (
      <button
        type="button"
        className={`mt-3 ${linkCls} inline-flex items-center gap-1`}
        onClick={() => setOpen(true)}
      >
        <CalendarClock className="h-4 w-4" /> Need more time?
      </button>
    )
  }
  return (
    <div className="mt-3 space-y-2 rounded-xl bg-muted p-3">
      <p className="text-sm text-foreground">
        Propose a new date{dueDate ? ` (it's ${day(dueDate)} now)` : ''}. {brandName} can accept or
        keep the date.
      </p>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className={fieldCls}
      />
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why? e.g. away games this weekend"
        className={`${fieldCls} min-h-16 resize-none`}
      />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={primaryCls}
          disabled={isLoading || !date}
          onClick={() =>
            create({
              campaignId: dealId,
              milestoneId,
              proposedDueDate: date,
              reason: reason.trim() || null,
            })
              .unwrap()
              .then(() => {
                toast('Sent to the brand')
                setOpen(false)
              })
              .catch(fail)
          }
        >
          Ask
        </button>
        <button type="button" className={secondaryCls} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  )
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

/** Shoots, store visits and signings the brand scheduled on this deal, with the athlete's RSVP */
export const EventsCard: React.FC<{ dealId: string; brandName: string }> = ({
  dealId,
  brandName,
}) => {
  const { data: events } = useGetCollaborationEventsQuery(dealId)
  const [rsvp, { isLoading }] = useRsvpCollaborationEventMutation()
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const list = (events ?? []).filter((e) => e.status !== 'cancelled')
  if (!list.length) return null
  const answer = (eventId: string, rsvpStatus: 'accepted' | 'declined' | 'proposed_changes') =>
    rsvp({ campaignId: dealId, eventId, rsvpStatus, athleteNote: note.trim() || undefined })
      .unwrap()
      .then(() => {
        toast('RSVP sent')
        setNoteFor(null)
        setNote('')
      })
      .catch(fail)
  return (
    <Card>
      <Eyebrow>Scheduled with {brandName}</Eyebrow>
      <ul className="mt-2 space-y-3">
        {list.map((e) => (
          <li key={e.id} className="text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{e.title}</p>
                <p className="text-muted-foreground">
                  {when(e.startAt)}
                  {e.endAt
                    ? ` – ${new Date(e.endAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                    : ''}
                </p>
                {e.location ? (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {e.location}
                  </p>
                ) : null}
              </div>
              <Pill
                tone={
                  e.rsvpStatus === 'accepted'
                    ? 'green'
                    : e.rsvpStatus === 'declined'
                      ? 'grey'
                      : e.rsvpStatus === 'proposed_changes'
                        ? 'amber'
                        : 'blue'
                }
              >
                {e.rsvpStatus === 'accepted'
                  ? "I'll be there"
                  : e.rsvpStatus === 'declined'
                    ? "Can't make it"
                    : e.rsvpStatus === 'proposed_changes'
                      ? 'Asked to change'
                      : 'RSVP'}
              </Pill>
            </div>
            {e.rsvpStatus === 'pending' ? (
              <div className="mt-2 space-y-2">
                {noteFor === e.id ? (
                  <textarea
                    value={note}
                    onChange={(ev) => setNote(ev.target.value)}
                    placeholder="What would work better?"
                    className={`${fieldCls} min-h-16 resize-none`}
                  />
                ) : null}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    className={primaryCls}
                    disabled={isLoading}
                    onClick={() => answer(e.id, 'accepted')}
                  >
                    I'll be there
                  </button>
                  <button
                    type="button"
                    className={secondaryCls}
                    disabled={isLoading}
                    onClick={() =>
                      noteFor === e.id ? answer(e.id, 'proposed_changes') : setNoteFor(e.id)
                    }
                  >
                    {noteFor === e.id ? 'Send' : 'Change?'}
                  </button>
                  <button
                    type="button"
                    className={secondaryCls}
                    disabled={isLoading}
                    onClick={() => answer(e.id, 'declined')}
                  >
                    Can't
                  </button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  )
}
